/**
 * Operations data layer for the Command Center.
 *
 * Surfaces the *live* operational picture (today's crews / activities / tasks /
 * resources) by reading the SAME source the Dispatch Board renders from —
 * `useDispatchBoardDay(today)` (the real `GET /Dispatch` board-day composite).
 *
 * NOTE (2026-06-29): previously this composed the legacy planner queries
 * (`/Planner/cards`, crew-load, asset-load). Those read a different/legacy
 * projection that the new DispatchActivity model doesn't populate, so the
 * dashboard showed 0s while the board showed real activities. Reading the
 * board-day directly keeps the two surfaces consistent.
 */
import * as React from 'react';

import { useDispatchBoardDay } from '@/features/dispatch-board/api/hooks';
import type {
  DispatchBoardDay,
  DispatchCrewJobActivity,
} from '@/features/dispatch-board/types';

/** Local-date `yyyy-MM-dd` for a Date (avoids UTC off-by-one from toISOString). */
export function toLocalDateKey(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** Today as a `yyyy-MM-dd` local key. */
export function todayKey(): string {
  return toLocalDateKey(new Date());
}

/** Monday of the current week as a `yyyy-MM-dd` local key. */
export function weekStartKey(): string {
  const d = new Date();
  const dow = d.getDay(); // 0 = Sun … 6 = Sat
  const diff = dow === 0 ? 6 : dow - 1; // days since Monday
  d.setDate(d.getDate() - diff);
  return toLocalDateKey(d);
}

/** Breakdown of today's activities by lifecycle status. */
export interface TodayStatusCounts {
  scheduled: number;
  inProgress: number;
  pendingReview: number;
  approved: number;
  cancelled: number;
}

export interface OperationsPulse {
  isLoading: boolean;
  isError: boolean;
  /** Total activities scheduled today (crew-assigned + standalone). */
  activitiesToday: number;
  byStatus: TodayStatusCounts;
  /** Sum of task counts across today's activities. */
  tasksDue: number;
  /** Crews running at least one activity today. */
  crewsActive: number;
  /** Staff on crews today (allocated), and total roster. */
  staffDeployed: number;
  staffTotal: number;
  /** Assets pinned to a crew today vs total fleet. */
  assetsDeployed: number;
  assetsTotal: number;
  /** Activities in progress whose scheduled end is already in the past. */
  atRisk: number;
  /** Work-order tasks not yet placed on the board. */
  unassignedBacklog: number;
}

/**
 * `useOperationsPulse` — the live "today" snapshot for the command center's
 * operational pulse strip, derived from the real dispatch board day so it
 * always matches what the dispatcher sees on the board.
 */
export function useOperationsPulse(): OperationsPulse {
  const date = todayKey();
  const { data, isLoading, isError } = useDispatchBoardDay(date);

  const day: DispatchBoardDay | undefined = data;
  const crews = day?.crews ?? [];
  const standalone = day?.standaloneActivities ?? [];

  // Every activity scheduled for today = crew-assigned jobs + standalone cards.
  const activities: DispatchCrewJobActivity[] = [
    ...crews.flatMap((c) => c.jobs ?? []),
    ...standalone,
  ];

  const now = Date.now();
  const byStatus: TodayStatusCounts = {
    scheduled: 0,
    inProgress: 0,
    pendingReview: 0,
    approved: 0,
    cancelled: 0,
  };
  let tasksDue = 0;
  let atRisk = 0;

  for (const a of activities) {
    switch (a.status) {
      case 'Scheduled':
        byStatus.scheduled += 1;
        break;
      case 'InProgress':
        byStatus.inProgress += 1;
        break;
      case 'Completed':
        byStatus.approved += 1; // "done" bucket
        break;
      case 'Cancelled':
        byStatus.cancelled += 1;
        break;
      default:
        break;
    }

    tasksDue += Array.isArray(a.tasks) ? a.tasks.length : 0;

    if (a.status === 'InProgress' && a.scheduledEnd) {
      const end = new Date(a.scheduledEnd).getTime();
      if (!Number.isNaN(end) && end < now) atRisk += 1;
    }
  }

  const staff = day?.staff ?? [];
  const assets = day?.assets ?? [];
  // Deployed assets = distinct assets pinned to a crew today (assignment lives
  // on each crew's `vehicles[]`, not on the asset record itself).
  const deployedAssetIds = new Set(
    crews.flatMap((c) => (c.vehicles ?? []).map((v) => v.assetId)),
  );

  return {
    isLoading,
    isError,
    activitiesToday: activities.length,
    byStatus,
    tasksDue,
    crewsActive: crews.filter((c) => (c.jobs?.length ?? 0) > 0).length,
    staffDeployed: staff.filter((s) => (s.allocationCount ?? 0) > 0).length,
    staffTotal: staff.length,
    assetsDeployed: deployedAssetIds.size,
    assetsTotal: assets.length,
    atRisk,
    unassignedBacklog: day?.unassigned?.length ?? 0,
  };
}

/* -------------------------------------------------------------------------- */
/* This-week insights — derived from the real dispatch board (replaces the    */
/* legacy /Planner/insights aggregator which the new dispatch data doesn't    */
/* populate). Aggregates the 7 days of the current week from useDispatchBoardDay.*/
/* -------------------------------------------------------------------------- */

const STATUS_FILL = {
  Scheduled: '#405189', // primary
  InProgress: '#f7b84b', // amber
  Completed: '#0ab39c', // green
  Cancelled: '#94a3b8', // slate
} as const;

const WEEKDAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

export interface OperationsWeek {
  isLoading: boolean;
  isError: boolean;
  statusBreakdown: Array<{ label: string; value: number; fill: string }>;
  kpis: {
    totalActivities: number;
    completedActivities: number;
    averageActivitiesPerDay: number;
  };
  completion: Array<{ label: string; planned: number; completed: number }>;
}

/**
 * `useOperationsWeek` — "this week" activity insights for the command center,
 * built from the real dispatch board day-by-day so the donut + planned-vs-
 * completed charts match what the dispatcher actually scheduled.
 */
export function useOperationsWeek(): OperationsWeek {
  // Seven fixed weekday keys (Mon…Sun of the current week). Stable per render.
  const weekDates = React.useMemo(() => {
    const base = new Date(`${weekStartKey()}T00:00:00`);
    return Array.from({ length: 7 }, (_, i) => {
      const d = new Date(base);
      d.setDate(base.getDate() + i);
      return toLocalDateKey(d);
    });
  }, []);

  // Fixed-count hook calls (one per weekday) — required for stable hook order.
  const q0 = useDispatchBoardDay(weekDates[0]);
  const q1 = useDispatchBoardDay(weekDates[1]);
  const q2 = useDispatchBoardDay(weekDates[2]);
  const q3 = useDispatchBoardDay(weekDates[3]);
  const q4 = useDispatchBoardDay(weekDates[4]);
  const q5 = useDispatchBoardDay(weekDates[5]);
  const q6 = useDispatchBoardDay(weekDates[6]);
  const queries = [q0, q1, q2, q3, q4, q5, q6];

  const counts = { Scheduled: 0, InProgress: 0, Completed: 0, Cancelled: 0 };
  const completion: OperationsWeek['completion'] = [];
  let total = 0;
  let completed = 0;
  let daysWithData = 0;

  queries.forEach((q, i) => {
    const day = q.data;
    const acts: DispatchCrewJobActivity[] = [
      ...(day?.crews ?? []).flatMap((c) => c.jobs ?? []),
      ...(day?.standaloneActivities ?? []),
    ];
    let dayCompleted = 0;
    for (const a of acts) {
      if (a.status in counts) counts[a.status as keyof typeof counts] += 1;
      if (a.status === 'Completed') dayCompleted += 1;
    }
    total += acts.length;
    completed += dayCompleted;
    if (acts.length > 0) daysWithData += 1;
    completion.push({
      label: WEEKDAY_LABELS[i],
      planned: acts.length,
      completed: dayCompleted,
    });
  });

  return {
    isLoading: queries.some((q) => q.isLoading),
    isError: queries.some((q) => q.isError),
    statusBreakdown: [
      { label: 'Scheduled', value: counts.Scheduled, fill: STATUS_FILL.Scheduled },
      { label: 'In progress', value: counts.InProgress, fill: STATUS_FILL.InProgress },
      { label: 'Completed', value: counts.Completed, fill: STATUS_FILL.Completed },
      { label: 'Cancelled', value: counts.Cancelled, fill: STATUS_FILL.Cancelled },
    ],
    kpis: {
      totalActivities: total,
      completedActivities: completed,
      averageActivitiesPerDay:
        daysWithData > 0 ? Math.round((total / daysWithData) * 10) / 10 : 0,
    },
    completion,
  };
}
